<template>
    <div class="kb-article">
        <div class="panel h1-panel">
            <div class="panel-heading">
                <h1><router-link to="/kb">Knowledge Base</router-link></h1>
            </div>
            <div class="panel-body">
                <h2>Article</h2>
            </div>
        </div>

        <section class="panel article-panel">
            <div class="panel-body">
                <div
                    class="article"
                    v-if="article">
                    <div class="article-heading">
                        <img
                            :src="getCoverImageUrl(article)"
                            v-if="getCoverImageUrl(article)">
                        <img
                            src="../assets/img/ttto-thumbnail.jpg"
                            v-else>
                        <h2>{{ article.title }}</h2>
                        <div>
                            <span>Edited {{ getLastEditTimeFromNow() }}</span>
                            <router-link
                                :to="getArticleEditorLocation()"
                                v-if="canEdit">
                                Edit
                            </router-link>
                        </div>
                    </div>
                    <div
                        class="article-body"
                        ref="article-body"
                        v-html="article.body"></div>

                    <div v-if="showExtras">
                        <hr>
                        <div class="article-extras">
                            <div
                                class="info-group"
                                v-if="article.categories.length > 0">
                                <div class="info-group-label">Categories</div>
                                <option-list :value="linkedCategories"></option-list>
                            </div>
                            <div
                                class="info-group"
                                v-if="article.termTypes.length > 0">
                                <div class="info-group-label">Term Types</div>
                                <option-list :value="linkedTermTypes"></option-list>
                            </div>
                            <div
                                class="info-group"
                                v-if="article.paths.length > 0">
                                <div class="info-group-label">Paths</div>
                                <option-list :value="linkedPaths"></option-list>
                            </div>
                            <div
                                class="info-group"
                                v-if="article.software.length > 0">
                                <div class="info-group-label">Software</div>
                                <software-list :value="linkedSoftware">
                                </software-list>
                            </div>
                            <div
                                class="info-group"
                                v-if="article.tags.length > 0">
                                <div class="info-group-label">Tags</div>
                                <tag-list :value="linkedTags">
                                </tag-list>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="loading-msg article-body" v-else>
                    Loading...
                </div>
            </div>
        </section>

        <kb-feedback></kb-feedback>
    </div>
</template>

<script>
    import moment from 'moment';
    import toastr from 'toastr';
    import Vue    from 'vue';

    import KbService    from '../services/knowledge-base';
    import OptionList   from './reusable/option-list';
    import PolyVideo    from './reusable/poly-video';
    import SoftwareList from './reusable/software-list';
    import TagList      from './reusable/tag-list';
    import KbFeedback   from './widgets/KbFeedback';

    export default {
        components: {
            OptionList,
            SoftwareList,
            TagList,
            KbFeedback,
        },

        data() {
            return {
                article: null,
                tags: [],

                isLoadingExtras: true,
                polyVideoComps: [],
            };
        },

        async mounted() {
            const articleId = this.$route.params.articleId;

            try {
                this.isLoadingExtras = true;

                await Promise.all([
                    this.$store.dispatch('ensureKbCategories'),
                    this.$store.dispatch('ensureAllPaths'),
                    this.$store.dispatch('ensureAllApps'),
                    KbService.getArticle(articleId).then(article => {
                        this.article = article;
                    }),
                ]);

                await Promise
                    .all(this.article.tags.map(KbService.getTag))
                    .then(tags => this.tags = tags);

                this.isLoadingExtras = false;

                // Now that we're done loading, add a view. Attempting to add 2
                // views within 15 minutes will cause an error. We could try to
                // catch that error specifically, but this works fine for now.
                KbService.createArticleView(articleId).catch(error => {
                    console.log('failed to record article view:', error);
                });

            } catch (error) {
                console.error('error loading kb article:', error);
                toastr.error(`
                    There was an error while loading the article.
                    Please try again later.
                `);
            }
        },

        beforeDestroy() {
            for (const polyVideo of this.polyVideoComps) {
                polyVideo.$destroy();
            }
            this.polyVideoComps = [];
        },

        methods: {
            getArticleEditorLocation() {
                const id = this.$route.params.articleId;
                return {
                    path: `/kb-article-editor/${id}`
                };
            },

            getCoverImageUrl(article) {
                if (article.coverImageUrl) {
                    return article.coverImageUrl;
                }
                if (article.categories.length > 0) {
                    const c = this.categories.find(c => {
                        return c.id === article.categories[0];
                    });
                    if (c) {
                        return c.imageUrl;
                    }
                }
                return null;
            },

            getLastEditTimeFromNow() {
                return moment(this.article.updatedAt).fromNow();
            },
        },

        computed: {
            categories() {
                if (!this.article) {
                    return [];
                }
                return this.article.categories.map(id => {
                    const cats = this.$store.getters.kbCategories;
                    return cats.find(c => c.id === id);
                });
            },

            paths() {
                if (!this.article) {
                    return [];
                }
                return this.article.paths.map(id => {
                    const paths = this.$store.getters.allPaths;
                    return paths.find(p => p.id === id);
                });
            },

            software() {
                if (!this.article) {
                    return [];
                }
                return this.article.software.map(id => {
                    const apps = this.$store.getters.allApps;
                    return apps.find(a => a.id === id);
                });
            },

            canEdit() {
                const userRoles = JSON.parse(localStorage.roles);
                const allowedRoles = [
                    'OnlineDirector',
                    'Supervisor',
                    'Faculty',
                ];
                return userRoles.some(r => allowedRoles.includes(r));
            },

            showExtras() {
                if (this.isLoadingExtras) {
                    return false;
                }
                let show = false;
                show = show || this.article.categories.length > 0;
                show = show || this.article.termTypes.length > 0;
                show = show || this.article.paths.length > 0;
                show = show || this.article.software.length > 0;
                show = show || this.article.tags.length > 0;
                return show;
            },

            linkedCategories() {
                return this.categories.map(c => {
                    return {
                        label: c.label,
                        href: '/kb-search?categories=' + c.id,
                    };
                });
            },

            linkedTermTypes() {
                return this.article.termTypes.map(t => {
                    return {
                        label: t,
                        href: '/kb-search?term-types=' + t,
                    };
                });
            },

            linkedPaths() {
                return this.paths.map(p => {
                    return {
                        label: p.name,
                        href: '/kb-search?paths=' + p.id,
                    };
                });
            },

            linkedSoftware() {
                return this.software.map(s => {
                    return Object.assign({
                        href: '/kb-search?software=' + s.id,
                    }, s);
                });
            },

            linkedTags() {
                return this.tags.map(t => {
                    return Object.assign({
                        href: '/kb-search?tags=' + t.id,
                    }, t);
                });
            },
        },

        watch: {
            'article.body': function() {

                // Remove the current poly video components
                for (const polyVideo of this.polyVideoComps) {
                    polyVideo.$destroy();
                }
                this.polyVideoComps = [];

                // Update videos to use transcoded versions
                this.$nextTick(() => {
                    const bodyEl = this.$refs['article-body'];
                    const videoEls = bodyEl.querySelectorAll('video');
                    for (const videoEl of videoEls) {

                        // Wrap the video in the poly-video player
                        const src = videoEl.src;
                        const polyVideo = new Vue({
                            ...PolyVideo,
                            el: videoEl,
                            propsData: {
                                sources: [src],
                                persistPlaybackSpeed: true,
                                persistPlaybackQuality: true,
                                vjsOptions: {
                                    fluid: true,
                                    playbackRates: [0.7, 1.0, 1.25, 1.5, 2.0, 3.0],
                                },
                            },
                        });
                        this.polyVideoComps.push(polyVideo);
                    }
                });
            }
        },
    }
</script>

<style lang="scss" scoped>
    .h1-panel {
        display: flex;

        .panel-heading,
        .panel-body {
            height: auto;
            width: auto;
            display: flex;
            align-items: center;
        }

        h1 {
            margin: 10px 0;

            a {
                color: inherit;
                text-decoration: none;
                &:hover {
                    color: #33ccff;
                }
            }
        }

        h2 {
            margin: 10px 15px;
            font-size: 20px;
        }
    }

    .article-panel {
        .panel-body {
            max-width: 800px;
            margin: 0 auto;
        }

        .article-heading {
            margin: 0 0 10px 0;
            padding: 40px 20px;
            position: relative;
            z-index: 0;
            overflow: hidden;

            &::before {
                content: '';
                position: absolute;
                top: 0;
                left: 0;
                width: 3px;
                height: 120px;
                background-color: #33ccff;
            }

            img {
                position: absolute;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                z-index: -2;
                object-fit: cover;
                mask-image: linear-gradient(
                    to bottom,
                    rgba(0, 0, 0, 0.6),
                    rgba(0, 0, 0, 0)
                );
            }

            h2 {
                margin: 0 0 15px;
            }

            span {
                font-style: italic;
            }

            a {
                margin-left: 10px;
            }
        }

        .article-body {
            // Used instead of .panel-body padding so that it can collapse with
            // the margin from the last article body element
            margin-bottom: 40px;
            padding: 0 20px;
        }

        hr {
            width: 60%;
            margin: 40px auto;
        }

        .article-extras {
            // Used instead of .panel-body padding so that it can collapse with
            // the margin from the last article body element
            margin-bottom: 40px;
            padding: 0 20px;

            .info-group {
                margin: 20px 0 10px;
            }

            .info-group-label {
                font-size: 16px;
            }
        }

        .loading-msg {
            display: flex;
            justify-content: center;
            align-items: center;
            height: 100px;
            width: 100%;
            font-style: italic;
            padding: 0 20px;
        }
    }
</style>

<style lang="scss">
    .kb-article {
        h3 {
            margin: 50px 0 30px;
        }

        h4 {
            margin: 40px 0 20px;
            font-size: 20px;
        }

        p {
            font-size: 16px;
            line-height: 1.6;
            margin: 0 0 20px 0;
        }

        li > p {
            margin: 0 0 10px 0;
        }

        > ul, > ol {
            margin: 0 0 20px 0 !important;
        }

        figure {
            margin: 30px auto 20px;
            width: 80%;

            video,
            iframe,
            iframe-wrapper,
            figcaption {
                width: 100%;
            }

            img,
            video,
            iframe {
                border: 4px solid #363636;
            }

            img {
                display: block;
                margin: 0 auto;
                height: auto;
                max-height: 450px;
                object-fit: contain;
            }

            .iframe-wrapper {
                position: relative;
                padding-top: 56.25%; // 16:9 aspect ratio

                iframe {
                    position: absolute;
                    top: 0;
                    left: 0;
                    width: 100%;
                    height: 100%;
                }
            }
        }

        figcaption {
            color: #b0b0b0;
            font-size: 14px;
            font-style: italic;
            margin-top: 10px;
            position: relative;
            padding: 10px 10px 10px 35px;
            &::before {
                content: '\2014';
                position: absolute;
                left: 10px;
            }
        }

        .software-list {
            > li {
                width: 100px;
            }
        }

        .kb-feedback-panel {
            max-width: 800px;
            margin: 0 auto 30px;
        }
    }
</style>



// WEBPACK FOOTER //
// KbArticle.vue?bcb82f9e